import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { X, Coins, MessageCircle, Sparkles, ShoppingBag } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierStyle = {
  bronze: 'from-orange-500/20 to-amber-700/20 border-amber-700/40',
  prata: 'from-slate-400/20 to-slate-600/20 border-slate-400/40',
  ouro: 'from-amber-300/20 to-amber-500/20 border-amber-400/50',
};

const StoreModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const reload = async () => {
    try {
      const { data } = await axios.get(`${API}/fantasy/wallet`);
      setData(data);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const requestWithdraw = async () => {
    if (!data) return;
    const min = data.withdraw_min_pc || 500;
    const raw = window.prompt(`Quanto queres sacar em ProCoins? (mín ${min} PC · máx ${data.withdrawable} PC · taxa ${Math.round((data.withdraw_fee_pct || 0) * 100)}%)`, String(data.withdrawable));
    if (!raw) return;
    const amount = parseInt(raw, 10);
    if (!Number.isFinite(amount) || amount <= 0) return;
    setWithdrawing(true); setErr('');
    try {
      const { data: res } = await axios.post(`${API}/fantasy/wallet/withdraw`, { amount_pc: amount });
      alert(`✅ Pedido enviado!\n\nBruto: ${amount} PC\nTaxa: ${res.fee_pc} PC\nRecebes: ${res.net_pc} PC = ${res.net_eur}€\n\nO admin vai pagar por MB WAY em breve.`);
      await reload();
    } catch (ex) { setErr(formatApiError(ex)); }
    finally { setWithdrawing(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur flex items-start sm:items-center justify-center p-3 overflow-y-auto" onClick={onClose} data-testid="store-modal">
      <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-2xl w-full my-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-white/10 px-5 py-3">
          <div className="flex items-center gap-2 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
              <ShoppingBag className="w-4 h-4 text-slate-950" />
            </div>
            <div className="min-w-0">
              <div className="text-white font-black">Loja de ProCoins</div>
              <div className="text-[10px] uppercase tracking-widest text-amber-300/80">1 € = 100 PC · pagamento por MB WAY</div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-slate-300" data-testid="store-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5">
          {loading ? <div className="text-slate-300 text-sm">A carregar...</div>
            : err ? <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>
            : (
              <>
                <div className="mb-4 grid grid-cols-2 gap-2">
                  <div className="flex items-center gap-2 text-xs bg-amber-500/10 border border-amber-400/30 rounded-xl px-3 py-2 text-amber-100">
                    <Coins className="w-4 h-4 text-amber-300 shrink-0" />
                    <div><div className="text-[10px] text-amber-300/80 uppercase tracking-wider">Total</div><div className="font-black text-sm">{Number(data.balance || 0).toLocaleString('pt-PT')} PC</div></div>
                  </div>
                  <div className="flex items-center justify-between gap-2 text-xs bg-emerald-500/10 border border-emerald-400/30 rounded-xl px-3 py-2 text-emerald-100">
                    <div className="flex items-center gap-2 min-w-0">
                      <Sparkles className="w-4 h-4 text-emerald-300 shrink-0" />
                      <div className="min-w-0"><div className="text-[10px] text-emerald-300/80 uppercase tracking-wider">Sacável</div><div className="font-black text-sm truncate">{Number(data.withdrawable || 0).toLocaleString('pt-PT')} PC</div></div>
                    </div>
                    <button onClick={requestWithdraw} disabled={withdrawing || (data.withdrawable || 0) < (data.withdraw_min_pc || 500)}
                      className="bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed text-slate-900 font-black text-[10px] rounded-full px-2.5 py-1 shrink-0"
                      data-testid="withdraw-btn"
                      title={`Min ${data.withdraw_min_pc} PC · taxa ${Math.round((data.withdraw_fee_pct || 0)*100)}%`}>
                      SACAR
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {(data.packages || []).map((p) => (
                    <div key={p.key}
                      className={`bg-gradient-to-br ${tierStyle[p.key] || 'from-slate-800/60 to-slate-900/60 border-white/10'} border rounded-2xl p-4 flex flex-col`}
                      data-testid={`store-pkg-${p.key}`}>
                      <div className="flex items-center justify-between">
                        <div className="text-white font-black text-lg">{p.label}</div>
                        <div className="text-slate-300 text-xs">{p.eur}€</div>
                      </div>
                      <div className="mt-2 text-3xl font-black text-amber-300 tabular-nums leading-tight">
                        {Number(p.total).toLocaleString('pt-PT')}
                        <span className="text-sm text-amber-200/70 ml-1">PC</span>
                      </div>
                      {p.bonus > 0 && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[11px] text-emerald-300 self-start bg-emerald-500/10 border border-emerald-400/30 rounded-full px-2 py-0.5">
                          <Sparkles className="w-3 h-3" /> +{p.bonus} bónus
                        </div>
                      )}
                      <a href={p.wa_link} target="_blank" rel="noreferrer"
                        className="mt-4 inline-flex items-center justify-center gap-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black rounded-lg px-3 py-2 text-sm"
                        data-testid={`store-buy-${p.key}`}>
                        <MessageCircle className="w-4 h-4" /> Pagar via MB WAY
                      </a>
                    </div>
                  ))}
                </div>

                <div className="mt-5 text-[11px] text-slate-400 leading-relaxed">
                  Depois de pagar por MB WAY e enviar o comprovativo no WhatsApp, o admin credita os ProCoins na tua carteira. Os ProCoins são usados <b className="text-slate-200">apenas</b> no módulo de Apostas — o orçamento 100 €M da equipa é fictício e não pode ser convertido.
                </div>

                {(data.transactions || []).length > 0 && (
                  <div className="mt-5">
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">Histórico</div>
                    <div className="max-h-36 overflow-y-auto space-y-1">
                      {data.transactions.map((t) => (
                        <div key={t.id} className="flex items-center justify-between text-xs bg-slate-800/40 rounded-lg px-2 py-1.5">
                          <div className="text-slate-300 truncate">{t.reason}</div>
                          <div className={`font-black tabular-nums ${t.delta >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{t.delta > 0 ? '+' : ''}{t.delta} PC</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default StoreModal;
